'use client';

/**
 * useAutoApprovedTools — which tools this user has told Meridian to run without
 * a confirmation card.
 *
 * Loads and saves through `{apiBaseUrl}/auto-approved-tools`. The GET is the
 * only source of the GRANTABLE vocabulary too: that set is derived on the
 * server (`pre-authorizable-tools`) and deliberately not shipped to the
 * browser, so a client that guessed at it would offer toggles the server then
 * refuses. Anything not in `grantable` renders no toggle at all.
 *
 * Saves are optimistic and self-correcting: the UI moves immediately, and the
 * server's echoed selection replaces local state on success. On failure the
 * previous selection is restored and the error surfaced, because a toggle that
 * silently snaps back is how someone believes they granted something they did
 * not.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthToken } from '../lib/api/authenticated-fetch.js';

/** Cross-instance sync signal, so two mounted copies cannot disagree. */
const AUTO_APPROVED_TOOLS_CHANGED_EVENT = 'meridian:auto-approved-tools-changed';

interface AutoApprovedToolsResponse {
  grantable?: string[];
  selected?: string[];
  max?: number;
  error?: string;
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

export interface UseAutoApprovedToolsOptions {
  apiBaseUrl?: string;
  /** Skip fetching entirely (e.g. a closed modal). */
  enabled?: boolean;
}

export function useAutoApprovedTools(options: UseAutoApprovedToolsOptions = {}) {
  const { apiBaseUrl = '/api/meridian', enabled = true } = options;

  const [grantable, setGrantable] = useState<readonly string[]>([]);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [max, setMax] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savingTool, setSavingTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Guards a save that resolves after unmount (a modal closed mid-request).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/auto-approved-tools`, {
        headers: buildHeaders(),
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load approval settings');
      const data = (await res.json()) as AutoApprovedToolsResponse;
      if (!mounted.current) return;
      setGrantable(data.grantable ?? []);
      setSelected(data.selected ?? []);
      setMax(typeof data.max === 'number' ? data.max : null);
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load approval settings');
    } finally {
      if (mounted.current) setIsLoading(false);
    }
  }, [apiBaseUrl, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const onChanged = () => {
      load();
    };
    window.addEventListener(AUTO_APPROVED_TOOLS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(AUTO_APPROVED_TOOLS_CHANGED_EVENT, onChanged);
  }, [enabled, load]);

  const grantableSet = useMemo(() => new Set(grantable), [grantable]);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const setAutoApproved = useCallback(
    async (toolName: string, next: boolean): Promise<boolean> => {
      if (!grantableSet.has(toolName)) return false;

      const previous = selected;
      const optimistic = next
        ? [...new Set([...selected, toolName])].sort()
        : selected.filter((name) => name !== toolName);

      setSelected(optimistic);
      setSavingTool(toolName);
      setError(null);

      try {
        const res = await fetch(`${apiBaseUrl}/auto-approved-tools`, {
          method: 'PUT',
          headers: buildHeaders(),
          credentials: 'include',
          body: JSON.stringify({ tools: optimistic }),
        });
        const data = (await res.json().catch(() => ({}))) as AutoApprovedToolsResponse;
        if (!res.ok) throw new Error(data.error ?? 'Failed to save approval settings');
        if (!mounted.current) return true;
        setSelected(data.selected ?? optimistic);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event(AUTO_APPROVED_TOOLS_CHANGED_EVENT));
        }
        return true;
      } catch (e) {
        if (!mounted.current) return false;
        // Put it back. A toggle that stays on after a failed save is a lie
        // about what the server will actually do on the next turn.
        setSelected(previous);
        setError(e instanceof Error ? e.message : 'Failed to save approval settings');
        return false;
      } finally {
        if (mounted.current) setSavingTool(null);
      }
    },
    [apiBaseUrl, grantableSet, selected]
  );

  return {
    /** Tool names this user is allowed to pre-authorize. */
    grantableSet,
    /** Tool names currently pre-authorized. */
    selectedSet,
    /** Server's cap on how many may be granted, or null while unknown. */
    max,
    isLoading,
    savingTool,
    error,
    reload: load,
    setAutoApproved,
  };
}
